import { ArrowRight, ExternalLink } from "lucide-react";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import type { HeroSection, Media } from "../product-types";
import { DecorationLayer, ImagePlaceholder } from "../shared/primitives";
import { reveal } from "../shared/reveal";

/** Deep-blue → brand-blue → cyan. Left side stays dark for white-text contrast. */
const HERO_GRADIENT = "linear-gradient(120deg, #0b3f93 0%, #1688f3 52%, #4aa8ff 100%)";

const WHITE_GRID = {
  backgroundImage:
    "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
  backgroundSize: "48px 48px",
  maskImage: "radial-gradient(ellipse 80% 70% at 30% 0%, black 30%, transparent 75%)",
  WebkitMaskImage: "radial-gradient(ellipse 80% 70% at 30% 0%, black 30%, transparent 75%)",
};

function HeroMedia({ media }: { media: Media }) {
  if (media.type === "placeholder") {
    return <ImagePlaceholder label={media.label} className={media.className ?? "aspect-[3/2]"} />;
  }
  if (media.type === "image") {
    return (
      <>
        {media.auroraClassName ? (
          <div
            aria-hidden
            className={media.auroraClassName}
            style={{ background: "var(--gradient-aurora)" }}
          />
        ) : null}
        <div
          className={cn(
            "relative overflow-hidden rounded-2xl border border-border bg-card",
            media.shadowGlow && "shadow-glow",
          )}
        >
          <img
            src={media.src}
            alt={media.alt}
            width={media.width}
            height={media.height}
            loading={media.loading}
            fetchPriority="high"
            className="block h-auto w-full object-cover"
          />
        </div>
      </>
    );
  }
  return null;
}

export function Hero({ section }: { section: HeroSection }) {
  const gradient = section.tone === "gradient";

  return (
    <section
      className={cn(
        "relative overflow-hidden",
        gradient ? "border-b border-white/10 text-white" : "border-b border-border/60",
        section.bgClassName,
      )}
      style={gradient ? { background: HERO_GRADIENT } : undefined}
    >
      {gradient ? (
        <>
          <div
            aria-hidden
            className="pointer-events-none absolute inset-0 -z-10 opacity-[0.15]"
            style={WHITE_GRID}
          />
          <div aria-hidden className="pointer-events-none absolute inset-0 -z-10 animate-sheen" />
          <div
            aria-hidden
            className="pointer-events-none absolute -right-24 -top-28 -z-10 h-[32rem] w-[32rem] rounded-full bg-white/20 blur-3xl"
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-32 -left-20 -z-10 h-[26rem] w-[26rem] rounded-full bg-[#8EE3F5]/30 blur-3xl"
          />
        </>
      ) : (
        section.decorations.map((decoration) => (
          <DecorationLayer key={decoration.className} decoration={decoration} />
        ))
      )}

      <div className="mx-auto grid max-w-7xl grid-cols-1 gap-12 px-6 py-20 lg:grid-cols-[1fr_1.15fr] lg:items-center lg:py-28">
        <div className="animate-fade-up">
          {gradient ? (
            <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1 text-xs font-medium text-white ring-1 ring-white/25 backdrop-blur">
              {section.eyebrow}
            </span>
          ) : (
            <p className="text-sm font-medium text-primary">{section.eyebrow}</p>
          )}
          <h1
            className={cn(
              "mt-3 text-4xl font-semibold leading-[1.05] tracking-tight sm:text-6xl",
              gradient ? "text-white" : "text-foreground",
            )}
          >
            {section.headline}
          </h1>
          <p
            className={cn(
              "mt-6 max-w-xl text-lg",
              gradient ? "text-white/85" : "text-muted-foreground",
            )}
          >
            {section.sub}
          </p>
          <div className="mt-8 flex flex-col gap-3 sm:flex-row">
            <Button
              asChild
              size="lg"
              className={cn(
                "h-11 rounded-lg px-6 font-medium",
                gradient
                  ? "bg-white text-[#0b3f93] shadow-lg shadow-black/10 hover:bg-white/90"
                  : section.primaryGlow && "animate-glow",
              )}
            >
              <a href={section.contactHref}>
                {section.contactLabel}
                <ArrowRight className="ml-1 h-4 w-4" />
              </a>
            </Button>
            <Button
              asChild
              size="lg"
              variant="outline"
              className={cn(
                "h-11 rounded-lg px-6 font-medium backdrop-blur",
                gradient &&
                  "border-white/40 bg-transparent text-white hover:bg-white/10 hover:text-white",
              )}
            >
              <a href={section.distributorHref}>{section.distributorLabel}</a>
            </Button>
          </div>
          <a
            href={section.compareHref}
            className={cn(
              "mt-5 inline-flex items-center gap-1.5 text-xs transition-colors",
              gradient
                ? "text-white/80 hover:text-white"
                : "text-muted-foreground hover:text-foreground",
            )}
          >
            <ExternalLink className="h-3 w-3" />
            {section.compareLabel}
          </a>
        </div>

        <div className="relative animate-fade-up" style={reveal(120)}>
          <HeroMedia media={section.media} />
        </div>
      </div>
    </section>
  );
}
